/**
 * Community Store
 *
 * Manages community state including memberships, feeds, and social features.
 * Supports 6 community types: school (full_school, grade, class), private/friend,
 * and public. Handles three feed types (Bucket List, Track Record, Discussion),
 * challenge assignments within class communities, and batch sharing of track records.
 *
 * @module useCommunityStore
 *
 * @example
 * ```tsx
 * import { useCommunityStore, selectMyCommunities, selectCurrentFeed } from '@/store/useCommunityStore'
 *
 * function CommunityPage() {
 *   const { fetchMyCommunities, fetchFeed } = useCommunityStore()
 *   const communities = useCommunityStore(selectMyCommunities)
 *   const feed = useCommunityStore(selectCurrentFeed)
 *
 *   useEffect(() => { fetchMyCommunities() }, [])
 *
 *   return <CommunityList items={communities} feed={feed} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  Community,
  CommunityMembership,
  CommunityInvitation,
  CommunityType,
  FeedItem,
  FeedComment,
  CommunityFilters,
  CommunityVisibility,
  JoinModel,
  DiscussionPosting,
  CommunityRole,
  SchoolCommunityLevel,
  CommunityAssignment,
  CommunityStudentProgress,
  ContentReport,
  ModerationLogEntry,
  ModerationActionType,
} from '../types'
import { communityApi, moderationApi } from '../api'

interface CommunityState {
  // Communities list
  communities: Community[]
  myCommunities: Community[]

  // School/institutional communities (separated by level)
  schoolCommunities: Community[]
  myFullSchoolCommunity: Community | null
  myGradeCommunity: Community | null
  myClassCommunity: Community | null

  // Personal communities (all non-institutional types)
  personalCommunities: Community[]

  // Current community (detail view)
  currentCommunity: Community | null
  currentMembers: CommunityMembership[]
  currentFeed: FeedItem[]

  // Invitations
  myPendingInvitations: CommunityInvitation[]
  communityInvitations: CommunityInvitation[]
  pendingJoinRequests: CommunityMembership[]
  isLoadingInvitations: boolean

  // Challenge Assignments (for class communities)
  currentAssignment: CommunityAssignment | null
  communityAssignments: CommunityAssignment[]
  assignmentProgress: CommunityStudentProgress[]
  isLoadingAssignments: boolean

  // Loading states
  isLoading: boolean
  isLoadingMembers: boolean
  isLoadingFeed: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions - Communities
  fetchCommunities: (filters?: CommunityFilters) => Promise<void>
  fetchMyCommunities: () => Promise<void>
  fetchCommunityById: (id: string) => Promise<void>

  // Actions - School/Institutional Communities
  fetchSchoolCommunities: (schoolId: string) => Promise<void>
  fetchMySchoolCommunityByLevel: (
    schoolId: string,
    level: SchoolCommunityLevel,
    gradeLevel?: string,
    classId?: string
  ) => Promise<void>

  // Actions - Personal Communities
  fetchPersonalCommunities: () => Promise<void>
  fetchDiscoverableCommunities: () => Promise<void>
  createCommunity: (data: {
    name: string
    description?: string
    type: CommunityType
    visibility?: CommunityVisibility
    joinModel?: JoinModel
    discussionPosting?: DiscussionPosting
    giftingDefault?: boolean
    avatarUrl?: string
    coverImageUrl?: string
  }) => Promise<Community | null>
  updateCommunity: (
    id: string,
    data: {
      name?: string
      description?: string
      visibility?: CommunityVisibility
      joinModel?: JoinModel
      discussionPosting?: DiscussionPosting
      giftingDefault?: boolean
      trackRecordCommentsEnabled?: boolean
      avatarUrl?: string
      bannerUrl?: string
    }
  ) => Promise<boolean>
  joinCommunity: (communityId: string) => Promise<boolean>
  leaveCommunity: (communityId: string) => Promise<boolean>

  deleteCommunity: (communityId: string) => Promise<boolean>

  // Actions - Members
  fetchMembers: (communityId: string) => Promise<void>
  updateMemberRole: (membershipId: string, role: CommunityRole) => Promise<boolean>
  searchUsersToInvite: (communityId: string, query: string) => Promise<Array<{ id: string; displayName: string; avatarUrl?: string; alreadyMember: boolean }>>
  inviteMember: (communityId: string, targetUserId: string) => Promise<boolean>
  updateMyGiftingOverride: (communityId: string, override: 'allowed' | 'blocked' | null) => Promise<boolean>

  // Actions - Feed
  fetchFeed: (communityId: string, page?: number) => Promise<void>
  createPost: (
    communityId: string,
    data: {
      textContent?: string
      trackRecordId?: string
      challengeId?: string
    }
  ) => Promise<boolean>
  deletePost: (feedItemId: string) => Promise<boolean>
  likePost: (feedItemId: string) => Promise<boolean>

  // Actions - Comments
  addComment: (feedItemId: string, content: string) => Promise<boolean>
  getComments: (feedItemId: string) => Promise<FeedComment[]>

  // Actions - Batch Share
  batchShare: (trackRecordId: string, communityIds: string[], textContent?: string) => Promise<boolean>

  // Actions - Invitations
  sendInvitation: (communityId: string, data: { userId?: string; email?: string }) => Promise<CommunityInvitation | null>
  fetchMyPendingInvitations: () => Promise<void>
  fetchCommunityInvitations: (communityId: string) => Promise<void>
  acceptInvitation: (invitationId: string) => Promise<boolean>
  declineInvitation: (invitationId: string) => Promise<boolean>
  cancelInvitation: (invitationId: string) => Promise<boolean>

  // Actions - Join Requests
  requestToJoin: (communityId: string) => Promise<boolean>
  fetchPendingJoinRequests: (communityId: string) => Promise<void>
  approveJoinRequest: (membershipId: string) => Promise<boolean>
  rejectJoinRequest: (membershipId: string) => Promise<boolean>

  // Actions - Assignments
  fetchCurrentAssignment: (communityId: string) => Promise<void>
  fetchCommunityAssignments: (communityId: string, status?: 'active' | 'completed' | 'archived') => Promise<void>
  fetchAssignmentProgress: (assignmentId: string) => Promise<void>
  assignChallenge: (
    communityId: string,
    challengeId: string,
    data: { dueDate?: string; assignmentNote?: string }
  ) => Promise<CommunityAssignment | null>
  updateAssignment: (
    assignmentId: string,
    data: { dueDate?: string; assignmentNote?: string; status?: 'active' | 'completed' | 'archived' }
  ) => Promise<boolean>
  archiveAssignment: (assignmentId: string) => Promise<boolean>

  // Moderation state
  communityReports: ContentReport[]
  moderationLog: ModerationLogEntry[]
  isLoadingReports: boolean

  // Actions - Moderation
  fetchCommunityReports: (communityId: string, status?: string) => Promise<void>
  reviewReport: (reportId: string, decision: {
    status: 'action_taken' | 'dismissed'
    actionTaken?: ModerationActionType
    note?: string
  }) => Promise<boolean>
  muteMember: (communityId: string, userId: string, reason?: string) => Promise<boolean>
  unmuteMember: (communityId: string, userId: string) => Promise<boolean>
  fetchModerationLog: (communityId: string) => Promise<void>

  clearError: () => void
  reset: () => void
}

const initialState = {
  communities: [],
  myCommunities: [],
  schoolCommunities: [],
  myFullSchoolCommunity: null,
  myGradeCommunity: null,
  myClassCommunity: null,
  personalCommunities: [],
  currentCommunity: null,
  currentMembers: [],
  currentFeed: [],
  myPendingInvitations: [],
  communityInvitations: [],
  pendingJoinRequests: [],
  isLoadingInvitations: false,
  currentAssignment: null,
  communityAssignments: [],
  assignmentProgress: [],
  communityReports: [] as ContentReport[],
  moderationLog: [] as ModerationLogEntry[],
  isLoadingReports: false,
  isLoading: false,
  isLoadingMembers: false,
  isLoadingFeed: false,
  isLoadingAssignments: false,
  isSaving: false,
  error: null,
}

export const useCommunityStore = createWithEqualityFn<CommunityState>()((set, get) => ({
  ...initialState,

  // ============================================================================
  // Communities
  // ============================================================================

  fetchCommunities: async (filters) => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getCommunities(filters)

    if (response.success && response.data) {
      set({ communities: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load communities',
        isLoading: false,
      })
    }
  },

  fetchMyCommunities: async () => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getMyCommunities()

    if (response.success && response.data) {
      set({ myCommunities: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load communities',
        isLoading: false,
      })
    }
  },

  fetchCommunityById: async (id) => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getCommunityById(id)

    if (response.success) {
      set({ currentCommunity: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Community not found',
        isLoading: false,
      })
    }
  },

  // ============================================================================
  // School Communities
  // ============================================================================

  fetchSchoolCommunities: async (schoolId) => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getSchoolCommunities(schoolId)

    if (response.success && response.data) {
      set({ schoolCommunities: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load school communities',
        isLoading: false,
      })
    }
  },

  fetchMySchoolCommunityByLevel: async (schoolId, level, gradeLevel, classId) => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getSchoolCommunityByLevel(
      schoolId,
      level,
      gradeLevel,
      classId
    )

    if (response.success) {
      const community = response.data
      if (level === 'full_school') {
        set({ myFullSchoolCommunity: community, isLoading: false })
      } else if (level === 'grade') {
        set({ myGradeCommunity: community, isLoading: false })
      } else if (level === 'class') {
        set({ myClassCommunity: community, isLoading: false })
      } else {
        set({ isLoading: false })
      }
    } else {
      set({
        error: response.error ?? 'Failed to load school community',
        isLoading: false,
      })
    }
  },

  // ============================================================================
  // Personal Communities
  // ============================================================================

  fetchPersonalCommunities: async () => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getPersonalCommunities()

    if (response.success && response.data) {
      set({ personalCommunities: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load personal communities',
        isLoading: false,
      })
    }
  },

  fetchDiscoverableCommunities: async () => {
    set({ isLoading: true, error: null })

    const response = await communityApi.getDiscoverableCommunities()

    if (response.success && response.data) {
      set({ communities: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load communities',
        isLoading: false,
      })
    }
  },

  createCommunity: async (data) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.createCommunity(data)

    if (response.success && response.data) {
      set({
        myCommunities: [...get().myCommunities, response.data],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create community',
        isSaving: false,
      })
      return null
    }
  },

  updateCommunity: async (id, data) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.updateCommunity(id, data)

    if (response.success && response.data) {
      set({
        currentCommunity: response.data,
        myCommunities: get().myCommunities.map((c) =>
          c.id === id ? response.data! : c
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update community',
        isSaving: false,
      })
      return false
    }
  },

  joinCommunity: async (communityId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.joinCommunity(communityId)

    if (response.success) {
      // Refresh communities
      await get().fetchMyCommunities()
      set({ isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to join community',
        isSaving: false,
      })
      return false
    }
  },

  leaveCommunity: async (communityId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.leaveCommunity(communityId)

    if (response.success) {
      set({
        myCommunities: get().myCommunities.filter((c) => c.id !== communityId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to leave community',
        isSaving: false,
      })
      return false
    }
  },

  deleteCommunity: async (communityId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.deleteCommunity(communityId)

    if (response.success) {
      set({
        communities: get().communities.filter((c) => c.id !== communityId),
        myCommunities: get().myCommunities.filter((c) => c.id !== communityId),
        currentCommunity: get().currentCommunity?.id === communityId ? null : get().currentCommunity,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete community',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Members
  // ============================================================================

  fetchMembers: async (communityId) => {
    set({ isLoadingMembers: true })

    const response = await communityApi.getCommunityMembers(communityId)

    if (response.success && response.data) {
      set({ currentMembers: response.data, isLoadingMembers: false })
    } else {
      set({ isLoadingMembers: false })
    }
  },

  updateMemberRole: async (membershipId, role) => {
    const response = await communityApi.updateMemberRole(membershipId, role)

    if (response.success && response.data) {
      set({
        currentMembers: get().currentMembers.map((m) =>
          m.id === membershipId ? response.data! : m
        ),
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to update role' })
      return false
    }
  },

  searchUsersToInvite: async (communityId, query) => {
    const response = await communityApi.searchUsersToInvite(communityId, query)
    return response.success && response.data ? response.data : []
  },

  inviteMember: async (communityId, targetUserId) => {
    set({ isSaving: true })
    const response = await communityApi.inviteMember(communityId, targetUserId)

    if (response.success && response.data) {
      // Refresh member list
      const membersResponse = await communityApi.getCommunityMembers(communityId)
      if (membersResponse.success && membersResponse.data) {
        set({ currentMembers: membersResponse.data })
      }
      set({ isSaving: false })
      return true
    } else {
      set({ error: response.error ?? 'Failed to invite member', isSaving: false })
      return false
    }
  },

  updateMyGiftingOverride: async (communityId, override) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.updateMyGiftingOverride(communityId, override)

    if (response.success && response.data) {
      // Update the currentCommunity's membership if present
      const current = get().currentCommunity
      if (current && current.id === communityId && current.currentUserMembership) {
        set({
          currentCommunity: {
            ...current,
            currentUserMembership: {
              ...current.currentUserMembership,
              giftingPermissionOverride: override,
            },
          },
          isSaving: false,
        })
      } else {
        set({ isSaving: false })
      }
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update gifting preference',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Feed
  // ============================================================================

  fetchFeed: async (communityId, page = 1) => {
    set({ isLoadingFeed: true, error: null })

    const response = await communityApi.getCommunityFeed(communityId, page)

    if (response.success && response.data) {
      set({
        currentFeed: page === 1 ? response.data : [...get().currentFeed, ...response.data],
        isLoadingFeed: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load feed',
        isLoadingFeed: false,
      })
    }
  },

  createPost: async (communityId, data) => {
    set({ isSaving: true, error: null })

    const type = data.trackRecordId
      ? 'track_record'
      : data.challengeId
        ? 'challenge_share'
        : 'discussion'

    const response = await communityApi.createFeedPost(communityId, {
      textContent: data.textContent,
      trackRecordId: data.trackRecordId,
      challengeId: data.challengeId,
      type,
    })

    if (response.success && response.data) {
      set({
        currentFeed: [response.data, ...get().currentFeed],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to create post',
        isSaving: false,
      })
      return false
    }
  },

  deletePost: async (feedItemId) => {
    const response = await communityApi.deleteFeedPost(feedItemId)

    if (response.success) {
      set({
        currentFeed: get().currentFeed.filter((f) => f.id !== feedItemId),
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to delete post' })
      return false
    }
  },

  likePost: async (feedItemId) => {
    const response = await communityApi.likeFeedPost(feedItemId)

    if (response.success) {
      set({
        currentFeed: get().currentFeed.map((f) =>
          f.id === feedItemId ? { ...f, likeCount: f.likeCount + 1 } : f
        ),
      })
      return true
    }
    return false
  },

  // ============================================================================
  // Comments
  // ============================================================================

  addComment: async (feedItemId, content) => {
    const response = await communityApi.addFeedComment(feedItemId, content)

    if (response.success) {
      // Update comment count in feed
      set({
        currentFeed: get().currentFeed.map((f) =>
          f.id === feedItemId ? { ...f, commentCount: f.commentCount + 1 } : f
        ),
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to add comment' })
      return false
    }
  },

  getComments: async (feedItemId) => {
    const response = await communityApi.getFeedComments(feedItemId)

    if (response.success && response.data) {
      return response.data
    }
    return []
  },

  // ============================================================================
  // Batch Share
  // ============================================================================

  batchShare: async (trackRecordId, communityIds, textContent) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.batchShareTrackRecord(
      trackRecordId,
      communityIds,
      textContent
    )

    if (response.success) {
      set({ isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to share',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Invitations
  // ============================================================================

  sendInvitation: async (communityId, data) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.sendInvitation(communityId, data)

    if (response.success && response.data) {
      set({
        communityInvitations: [response.data, ...get().communityInvitations],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to send invitation',
        isSaving: false,
      })
      return null
    }
  },

  fetchMyPendingInvitations: async () => {
    set({ isLoadingInvitations: true })

    const response = await communityApi.getMyPendingInvitations()

    if (response.success && response.data) {
      set({ myPendingInvitations: response.data, isLoadingInvitations: false })
    } else {
      set({ isLoadingInvitations: false })
    }
  },

  fetchCommunityInvitations: async (communityId) => {
    set({ isLoadingInvitations: true })

    const response = await communityApi.getCommunityInvitations(communityId)

    if (response.success && response.data) {
      set({ communityInvitations: response.data, isLoadingInvitations: false })
    } else {
      set({ isLoadingInvitations: false })
    }
  },

  acceptInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.acceptInvitation(invitationId)

    if (response.success) {
      set({
        myPendingInvitations: get().myPendingInvitations.filter((inv) => inv.id !== invitationId),
        isSaving: false,
      })
      // Refresh communities
      await get().fetchPersonalCommunities()
      return true
    } else {
      set({
        error: response.error ?? 'Failed to accept invitation',
        isSaving: false,
      })
      return false
    }
  },

  declineInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.declineInvitation(invitationId)

    if (response.success) {
      set({
        myPendingInvitations: get().myPendingInvitations.filter((inv) => inv.id !== invitationId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to decline invitation',
        isSaving: false,
      })
      return false
    }
  },

  cancelInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.cancelInvitation(invitationId)

    if (response.success) {
      set({
        communityInvitations: get().communityInvitations.map((inv) =>
          inv.id === invitationId ? { ...inv, status: 'expired' as const } : inv
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to cancel invitation',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Join Requests
  // ============================================================================

  requestToJoin: async (communityId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.requestToJoin(communityId)

    if (response.success) {
      // Update the currentCommunity to reflect the pending request
      const current = get().currentCommunity
      if (current && current.id === communityId) {
        set({
          currentCommunity: {
            ...current,
            currentUserMembership: response.data ?? undefined,
          },
          isSaving: false,
        })
      } else {
        set({ isSaving: false })
      }
      return true
    } else {
      set({
        error: response.error ?? 'Failed to send join request',
        isSaving: false,
      })
      return false
    }
  },

  fetchPendingJoinRequests: async (communityId) => {
    set({ isLoadingInvitations: true })

    const response = await communityApi.getPendingJoinRequests(communityId)

    if (response.success && response.data) {
      set({ pendingJoinRequests: response.data, isLoadingInvitations: false })
    } else {
      set({ isLoadingInvitations: false })
    }
  },

  approveJoinRequest: async (membershipId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.approveJoinRequest(membershipId)

    if (response.success) {
      set({
        pendingJoinRequests: get().pendingJoinRequests.filter((r) => r.id !== membershipId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to approve request',
        isSaving: false,
      })
      return false
    }
  },

  rejectJoinRequest: async (membershipId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.rejectJoinRequest(membershipId)

    if (response.success) {
      set({
        pendingJoinRequests: get().pendingJoinRequests.filter((r) => r.id !== membershipId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to reject request',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Assignments
  // ============================================================================

  fetchCurrentAssignment: async (communityId) => {
    set({ isLoadingAssignments: true })

    const response = await communityApi.getCurrentAssignment(communityId)

    if (response.success) {
      set({ currentAssignment: response.data, isLoadingAssignments: false })
    } else {
      set({ isLoadingAssignments: false })
    }
  },

  fetchCommunityAssignments: async (communityId, status) => {
    set({ isLoadingAssignments: true })

    const response = await communityApi.getCommunityAssignments(communityId, status)

    if (response.success && response.data) {
      set({ communityAssignments: response.data, isLoadingAssignments: false })
    } else {
      set({ isLoadingAssignments: false })
    }
  },

  fetchAssignmentProgress: async (assignmentId) => {
    set({ isLoadingAssignments: true })

    const response = await communityApi.getAssignmentProgress(assignmentId)

    if (response.success && response.data) {
      set({ assignmentProgress: response.data, isLoadingAssignments: false })
    } else {
      set({ isLoadingAssignments: false })
    }
  },

  assignChallenge: async (communityId, challengeId, data) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.assignChallenge(communityId, challengeId, data)

    if (response.success && response.data) {
      set({
        currentAssignment: response.data,
        communityAssignments: [response.data, ...get().communityAssignments],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to assign challenge',
        isSaving: false,
      })
      return null
    }
  },

  updateAssignment: async (assignmentId, data) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.updateAssignment(assignmentId, data)

    if (response.success && response.data) {
      set({
        currentAssignment: get().currentAssignment?.id === assignmentId ? response.data : get().currentAssignment,
        communityAssignments: get().communityAssignments.map((a) =>
          a.id === assignmentId ? response.data! : a
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update assignment',
        isSaving: false,
      })
      return false
    }
  },

  archiveAssignment: async (assignmentId) => {
    set({ isSaving: true, error: null })

    const response = await communityApi.archiveAssignment(assignmentId)

    if (response.success) {
      set({
        currentAssignment: get().currentAssignment?.id === assignmentId ? null : get().currentAssignment,
        communityAssignments: get().communityAssignments.map((a) =>
          a.id === assignmentId ? { ...a, status: 'archived' as const } : a
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to archive assignment',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Moderation
  // ============================================================================

  fetchCommunityReports: async (communityId, status) => {
    set({ isLoadingReports: true, error: null })

    const response = await moderationApi.getCommunityReports(communityId, status)

    if (response.success && response.data) {
      set({ communityReports: response.data, isLoadingReports: false })
    } else {
      set({
        error: response.error ?? 'Failed to load reports',
        isLoadingReports: false,
      })
    }
  },

  reviewReport: async (reportId, decision) => {
    set({ isSaving: true, error: null })

    const response = await moderationApi.reviewReport(reportId, decision)

    if (response.success && response.data) {
      set({
        communityReports: get().communityReports.map((r) =>
          r.id === reportId ? response.data! : r
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to review report',
        isSaving: false,
      })
      return false
    }
  },

  muteMember: async (communityId, userId, reason) => {
    set({ isSaving: true, error: null })

    const response = await moderationApi.muteMember(communityId, userId, reason)

    if (response.success) {
      set({
        currentMembers: get().currentMembers.map((m) =>
          m.userId === userId ? { ...m, status: 'muted' as const, mutedAt: new Date().toISOString() } : m
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to mute member',
        isSaving: false,
      })
      return false
    }
  },

  unmuteMember: async (communityId, userId) => {
    set({ isSaving: true, error: null })

    const response = await moderationApi.unmuteMember(communityId, userId)

    if (response.success) {
      set({
        currentMembers: get().currentMembers.map((m) =>
          m.userId === userId ? { ...m, status: 'active' as const, mutedAt: undefined } : m
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to unmute member',
        isSaving: false,
      })
      return false
    }
  },

  fetchModerationLog: async (communityId) => {
    const response = await moderationApi.getModerationLog(communityId)

    if (response.success && response.data) {
      set({ moderationLog: response.data })
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select all communities (from discover/browse). */
export const selectCommunities = (state: CommunityState) => state.communities

/** Select communities the current user is a member of. */
export const selectMyCommunities = (state: CommunityState) => state.myCommunities

/** Select the community currently being viewed in the detail page. */
export const selectCurrentCommunity = (state: CommunityState) => state.currentCommunity

/** Select the feed items for the currently viewed community. */
export const selectCurrentFeed = (state: CommunityState) => state.currentFeed

// School community selectors

/** Select all communities for the user's school. */
export const selectSchoolCommunities = (state: CommunityState) => state.schoolCommunities

/** Select the user's full-school community (all students). */
export const selectMyFullSchoolCommunity = (state: CommunityState) => state.myFullSchoolCommunity

/** Select the user's grade-level community. */
export const selectMyGradeCommunity = (state: CommunityState) => state.myGradeCommunity

/** Select the user's class community (teacher-managed). */
export const selectMyClassCommunity = (state: CommunityState) => state.myClassCommunity

// Personal community selectors
export const selectPersonalCommunities = (state: CommunityState) => state.personalCommunities

// Private community selectors

/** Select friend/private communities the user belongs to. */
export const selectPrivateCommunities = (state: CommunityState) => state.privateCommunities

// Invitation selectors
export const selectMyPendingInvitations = (state: CommunityState) => state.myPendingInvitations
export const selectMyPendingInvitationCount = (state: CommunityState) => state.myPendingInvitations.length
export const selectPendingJoinRequests = (state: CommunityState) => state.pendingJoinRequests

// Assignment selectors

/** Select the active challenge assignment for a class community. */
export const selectCurrentAssignment = (state: CommunityState) => state.currentAssignment

/** Select all challenge assignments for a community (active, completed, archived). */
export const selectCommunityAssignments = (state: CommunityState) => state.communityAssignments

/** Select per-student progress for a challenge assignment. */
export const selectAssignmentProgress = (state: CommunityState) => state.assignmentProgress
